import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { PunttAvatar } from "./PunttAvatar";

import {
  AvatarWithInitials,
  AVATAR_SIZE_NORMAL,
} from "../../../components/AvatarWithInitials";
import { useSearchEnterpriseUsers } from "../../../utils/queries/askPuntt";
import {
  emailMentionId,
  getActiveMentionActors,
  MENTION_HIGHLIGHT_CLASS,
  PUNTT_MENTION_ID,
  renderMentionSegments,
  resolveMentionPayload,
  type MentionActor,
} from "../chatUtils";
import {
  buildNewEmailCandidate,
  shouldOfferNewEmailInvite,
} from "../utils/classifyInviteCandidate";

const COMPOSER_TEXT_CLASS =
  "min-h-[60px] w-full resize-none border-none bg-transparent p-2 text-base leading-6 text-puntt-neutral-gray-12 whitespace-pre-wrap break-words outline-none placeholder:text-puntt-neutral-gray-9";

export type MentionMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type Candidate = {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  isPuntt: boolean;
  isEmailInvite?: boolean;
};

const ACTIVE_TOKEN_FEED = /(^|\s)@(\w*)$/;
// Directory mode allows email-shaped queries (`@john@acme.com`).
const ACTIVE_TOKEN_DIRECTORY = /(^|\s)@([\w.@+-]*)$/;

function dedupeMembers(members: MentionMember[]): MentionMember[] {
  const seen = new Set<string>();
  const out: MentionMember[] = [];
  for (const m of members) {
    if (seen.has(m._id)) continue;
    seen.add(m._id);
    out.push(m);
  }
  return out;
}

/**
 * Controlled textarea with `@mention` autocomplete. Selecting a person grants
 * them chat/project access when the owner sends (see christo sendMessage).
 */
export function MentionTextarea({
  value,
  onChange,
  onSend,
  onPaste,
  members,
  rosterMembers = [],
  enableDirectorySearch = false,
  composerAriaLabel,
  placeholder,
  disabled,
  "data-testid": dataTestId,
}: {
  value: string;
  onChange: (
    value: string,
    mentions: string[],
    mentionEmails: string[],
  ) => void;
  onSend?: () => void;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  members: MentionMember[];
  /** Extra roster (e.g. project members) merged with chat allow-list members. */
  rosterMembers?: MentionMember[];
  enableDirectorySearch?: boolean;
  composerAriaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [selected, setSelected] = useState<MentionActor[]>([]);
  const tokenStartRef = useRef<number | null>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    bottom: number;
    width: number;
  } | null>(null);

  const activeTokenPattern = enableDirectorySearch
    ? ACTIVE_TOKEN_DIRECTORY
    : ACTIVE_TOKEN_FEED;

  const localMembers = useMemo(
    () => dedupeMembers([...members, ...rosterMembers]),
    [members, rosterMembers],
  );

  const trimmedQuery = query.trim();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const directorySearchQuery =
    trimmedQuery.length === 0 ? "" : debouncedSearchQuery;

  useEffect(() => {
    if (trimmedQuery === "") {
      setDebouncedSearchQuery("");
      return;
    }
    const handle = window.setTimeout(
      () => setDebouncedSearchQuery(trimmedQuery),
      250,
    );
    return () => window.clearTimeout(handle);
  }, [trimmedQuery]);

  const directorySearch = useSearchEnterpriseUsers(directorySearchQuery, {
    enabled: enableDirectorySearch && open,
    limit: 25,
  });
  const directoryLoading =
    enableDirectorySearch &&
    open &&
    trimmedQuery.length === 0 &&
    (directorySearch.isFetching || directorySearch.isLoading);

  const staticCandidates: Candidate[] = useMemo(() => {
    const base: Candidate[] = [
      { id: PUNTT_MENTION_ID, name: "Puntt", isPuntt: true },
    ];
    for (const m of localMembers) {
      base.push({
        id: m._id,
        name: m.name ?? m.email ?? t("mentionTextarea.memberFallback"),
        email: m.email,
        avatar: m.avatar ?? undefined,
        isPuntt: false,
      });
    }
    return base;
  }, [localMembers, t]);

  const directoryCandidates: Candidate[] = useMemo(() => {
    if (!enableDirectorySearch) return [];
    const fromSearch = (directorySearch.data?.users ?? []).map((u) => ({
      id: u._id,
      name: u.name ?? u.email ?? t("mentionTextarea.memberFallback"),
      email: u.email,
      avatar: u.avatar ?? undefined,
      isPuntt: false as const,
    }));
    const merged = new Map<string, Candidate>();
    for (const c of [...staticCandidates, ...fromSearch]) {
      if (c.id !== PUNTT_MENTION_ID) merged.set(c.id, c);
    }
    const emailCandidate = shouldOfferNewEmailInvite(
      query.trim(),
      directorySearch.data?.users ?? [],
    )
      ? buildNewEmailCandidate(query.trim())
      : null;
    const list = [staticCandidates[0], ...Array.from(merged.values())].filter(
      Boolean,
    ) as Candidate[];
    if (emailCandidate) {
      list.push({
        id: emailMentionId(emailCandidate.email),
        name: emailCandidate.email,
        email: emailCandidate.email,
        isPuntt: false,
        isEmailInvite: true,
      });
    }
    return list;
  }, [
    directorySearch.data?.users,
    enableDirectorySearch,
    query,
    staticCandidates,
    t,
  ]);

  const candidates = enableDirectorySearch
    ? directoryCandidates
    : staticCandidates;

  const filtered = useMemo(() => {
    if (!query) return candidates;
    const q = query.toLowerCase();
    return candidates.filter(
      (c) =>
        c.isEmailInvite ||
        c.name.toLowerCase().includes(q) ||
        (c.email?.toLowerCase().includes(q) ?? false),
    );
  }, [candidates, query]);

  const highlightSegments = useMemo(() => {
    const actors = getActiveMentionActors(value, selected);
    return renderMentionSegments(value, actors);
  }, [value, selected]);

  const syncBackdropScroll = () => {
    const ta = textareaRef.current;
    const backdrop = backdropRef.current;
    if (!ta || !backdrop) return;
    backdrop.scrollTop = ta.scrollTop;
    backdrop.scrollLeft = ta.scrollLeft;
  };

  useEffect(() => {
    if (!value) setSelected([]);
  }, [value]);

  useEffect(() => {
    setHighlight((h) => (h >= filtered.length ? 0 : h));
  }, [filtered.length]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const el = textareaRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setMenuPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 6,
        width: rect.width,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const emitChange = (next: string, nextSelected: MentionActor[]) => {
    const payload = resolveMentionPayload(next, nextSelected);
    onChange(next, payload.mentions, payload.mentionEmails);
  };

  const updateActiveToken = (text: string, caret: number) => {
    const match = text.slice(0, caret).match(activeTokenPattern);
    if (match) {
      setOpen(true);
      setQuery(match[2]);
      tokenStartRef.current = caret - match[2].length - 1;
    } else {
      setOpen(false);
      setQuery("");
      tokenStartRef.current = null;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    const caret = e.target.selectionStart ?? next.length;
    emitChange(next, selected);
    updateActiveToken(next, caret);
  };

  const selectCandidate = (candidate: Candidate | undefined) => {
    const el = textareaRef.current;
    const start = tokenStartRef.current;
    if (!candidate || !el || start == null) return;
    const caret = el.selectionStart ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(caret);
    const token = `@${candidate.name} `;
    const next = `${before}${token}${after}`;
    const actor: MentionActor = { id: candidate.id, name: candidate.name };
    const nextSelected = selected.some((s) => s.id === actor.id)
      ? selected
      : [...selected, actor];

    setSelected(nextSelected);
    setOpen(false);
    setQuery("");
    tokenStartRef.current = null;
    emitChange(next, nextSelected);

    const nextCaret = before.length + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (open && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % filtered.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        selectCandidate(filtered[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend?.();
    }
  };

  const activeOptionId =
    open && filtered.length > 0
      ? `${listboxId}-option-${filtered[highlight]?.id}`
      : undefined;

  const showDropdown =
    open && menuPos != null && (filtered.length > 0 || directoryLoading);

  const dropdown = showDropdown && menuPos && (
    <ul
      id={listboxId}
      role="listbox"
      aria-label={t("mentionTextarea.dropdownAriaLabel")}
      data-testid="ask-puntt-mention-dropdown"
      style={{
        position: "fixed",
        left: menuPos.left,
        bottom: menuPos.bottom,
        width: menuPos.width,
        minWidth: 220,
        maxWidth: 360,
      }}
      className="z-50 max-h-56 overflow-y-auto rounded-lg border border-puntt-neutral-gray-5 bg-base-white py-1 shadow-lg"
    >
      {directoryLoading && (
        <li
          role="status"
          aria-live="polite"
          data-testid="ask-puntt-mention-directory-loading"
          className="px-3 py-1.5 text-[12px] text-puntt-neutral-gray-10"
        >
          {t("mentionTextarea.loadingTeamMembers")}
        </li>
      )}
      {filtered.map((c, i) => (
        <li
          key={c.id}
          id={`${listboxId}-option-${c.id}`}
          role="option"
          aria-selected={i === highlight}
          data-testid={`ask-puntt-mention-option-${c.id}`}
          onMouseDown={(e) => {
            e.preventDefault();
            selectCandidate(c);
          }}
          onMouseEnter={() => setHighlight(i)}
          className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${
            i === highlight ? "bg-puntt-neutral-gray-3" : ""
          }`}
        >
          {c.isPuntt ? (
            <PunttAvatar size={AVATAR_SIZE_NORMAL} />
          ) : (
            <AvatarWithInitials
              size={AVATAR_SIZE_NORMAL}
              name={c.name}
              avatar={c.avatar}
              className="shrink-0"
            />
          )}
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium text-puntt-neutral-gray-12">
              {c.isEmailInvite
                ? t("mentionTextarea.inviteName", { name: c.name })
                : c.name}
            </span>
            {c.email && !c.isEmailInvite && (
              <span className="block truncate text-[11px] text-puntt-neutral-gray-10">
                {c.email}
              </span>
            )}
            {c.isEmailInvite && (
              <span className="block truncate text-[11px] text-puntt-neutral-gray-10">
                {t("mentionTextarea.newEmail")}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="relative" data-testid={dataTestId}>
      {dropdown && createPortal(dropdown, document.body)}
      <div className="relative">
        <div
          ref={backdropRef}
          aria-hidden="true"
          data-testid="ask-puntt-mention-highlight-backdrop"
          className={`pointer-events-none absolute inset-0 overflow-hidden ${COMPOSER_TEXT_CLASS} !text-transparent`}
        >
          {highlightSegments.map((seg, i) =>
            seg.type === "mention" ? (
              <span
                key={i}
                data-testid={`ask-puntt-composer-mention-${seg.id}`}
                className={`${MENTION_HIGHLIGHT_CLASS} text-transparent`}
              >
                {seg.label}
              </span>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </div>
        <textarea
          ref={textareaRef}
          data-testid="ask-puntt-chat-reply-input"
          aria-label={
            composerAriaLabel ?? t("mentionTextarea.composerAriaLabelDefault")
          }
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          onScroll={syncBackdropScroll}
          onBlur={() => setOpen(false)}
          className={`relative z-[1] !shadow-none selection:bg-puntt-accent-4 ${COMPOSER_TEXT_CLASS}`}
          style={{ outline: "none", boxShadow: "none" }}
        />
      </div>
    </div>
  );
}
